import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { UserPreferences, FarmingPackage, CartItem, PromoCode, OrderFrequency } from '@shared/types';
import { CONSTANTS } from '@/lib/farm-data';
import type { SupportedCountry } from '@/lib/currency';

const API_BASE = import.meta.env.VITE_API_BASE || '';
const DEBOUNCE_MS = 2000;
const OFFLINE_QUEUE_KEY = 'farmpack-cart-offline-queue';

interface OfflineOperation {
  type: 'save';
  items: { productId: string; quantity: number; frequency?: string }[];
  timestamp: number;
}

function getUserId(): string | null {
  try {
    const stored = localStorage.getItem('farmpack-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.userId || parsed.id || null;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

function getOfflineQueue(): OfflineOperation[] {
  try {
    const data = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveOfflineQueue(queue: OfflineOperation[]): void {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
}

function debounce<T extends (...args: any[]) => any>(fn: T, ms: number): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn(...args);
    }, ms);
  }) as T;
}

async function fetchCartFromAPI(): Promise<{ items: { productId: string; quantity: number; frequency?: string }[]; updatedAt: number } | null> {
  const userId = getUserId();
  if (!userId) return null;
  try {
    const res = await fetch(`${API_BASE}/api/users/cart`, {
      headers: { 'x-user-id': userId }
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.cart;
  } catch {
    return null;
  }
}

async function saveCartToAPI(items: { productId: string; quantity: number; frequency?: string }[]): Promise<boolean> {
  const userId = getUserId();
  if (!userId) return false;
  try {
    const res = await fetch(`${API_BASE}/api/users/cart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userId
      },
      body: JSON.stringify({ items })
    });
    return res.ok;
  } catch {
    const queue = getOfflineQueue();
    queue.push({ type: 'save', items, timestamp: Date.now() });
    saveOfflineQueue(queue);
    return false;
  }
}

async function processOfflineQueue(): Promise<void> {
  const queue = getOfflineQueue();
  if (queue.length === 0) return;
  const queueCopy = [...queue];
  let allSuccess = true;
  for (const op of queueCopy) {
    const success = await saveCartToAPI(op.items);
    if (!success) {
      allSuccess = false;
      break;
    }
  }
  if (allSuccess) {
    saveOfflineQueue([]);
  }
}

function mergeCartItems(
  local: { productId: string; quantity: number; frequency?: string }[],
  server: { productId: string; quantity: number; frequency?: string }[]
): { productId: string; quantity: number; frequency?: string }[] {
  const merged = new Map<string, { productId: string; quantity: number; frequency?: string }>();
  for (const item of server) {
    merged.set(item.productId, { ...item });
  }
  for (const item of local) {
    const existing = merged.get(item.productId);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      merged.set(item.productId, { ...item });
    }
  }
  return Array.from(merged.values());
}

export async function loadCartFromProfile(): Promise<CartItem[]> {
  const serverCart = await fetchCartFromAPI();
  if (!serverCart || serverCart.items.length === 0) return [];

  const localCart = useFarmStore.getState().cart;
  const localItems = localCart.map(i => ({ productId: i.product.id, quantity: i.quantity, frequency: i.frequency }));
  const mergedItems = mergeCartItems(localItems, serverCart.items);

  const { updateQuantity, addToCart } = useFarmStore.getState();
  const currentProducts = useFarmStore.getState().cart.map(i => i.product);
  
  const newItems: CartItem[] = [];
  for (const merged of mergedItems) {
    const existing = currentProducts.find(p => p.id === merged.productId);
    if (existing) {
      updateQuantity(merged.productId, merged.quantity, merged.frequency as OrderFrequency | undefined);
    } else {
      newItems.push({
        product: { id: merged.productId, name: '', category: 'feed', price: 0, description: '' },
        quantity: merged.quantity,
        subtotal: 0,
        frequency: merged.frequency as OrderFrequency | undefined
      });
    }
  }
  if (newItems.length > 0) {
    addToCart(newItems);
  }

  return useFarmStore.getState().cart;
}

export async function saveCartToProfile(): Promise<void> {
  const cart = useFarmStore.getState().cart;
  const items = cart.map(i => ({ productId: i.product.id, quantity: i.quantity, frequency: i.frequency }));
  await saveCartToAPI(items);
}

export function triggerCartSync(): void {
  const debouncedSave = debounce(saveCartToProfile, DEBOUNCE_MS);
  debouncedSave();
}

export async function initCartSync(): Promise<void> {
  await processOfflineQueue();
  window.addEventListener('online', processOfflineQueue);
}

interface FarmState {
  wizardStep: number;
  preferences: UserPreferences;
  recommendations: FarmingPackage[];
  recommendationSessionId: string | null;
  cart: CartItem[];
  activePromo: PromoCode | null;
  selectedCountry: SupportedCountry | null;
  setStep: (step: number) => void;
  setPreferences: (prefs: Partial<UserPreferences>) => void;
  setRecommendations: (pkgs: FarmingPackage[], sessionId?: string) => void;
  addToCart: (items: CartItem[]) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number, frequency?: OrderFrequency) => void;
  applyPromo: (code: string) => boolean;
  clearCart: () => void;
  resetWizard: () => void;
  setCountry: (country: SupportedCountry) => void;
}

const debouncedSave = debounce(saveCartToProfile, DEBOUNCE_MS);

export const useFarmStore = create<FarmState>()(
  persist(
    (set, get) => ({
      wizardStep: 0,
      preferences: {
        livestockType: 'poultry',
        experience: 'beginner',
        budget: 1000,
        spaceSqM: 50,
        location: 'Suburban',
        goal: 'side-income',
        timeAvailability: 'daily-attention',
        waterSource: 'borehole',
        trainingNeeded: false,
        includeTrainingUpsell: true,
        country: 'GH',
        spacePhoto: undefined,
        region: undefined,
        nearestLandmark: undefined,
        hasElectricity: false,
        hasWaterSource: true,
      },
      recommendations: [],
      recommendationSessionId: null,
      cart: [],
      activePromo: null,
      selectedCountry: null,
      setStep: (step) => set({ wizardStep: step }),
      setPreferences: (newPrefs) => set((state) => ({
        preferences: { ...state.preferences, ...newPrefs }
      })),
      setRecommendations: (pkgs, sessionId) => set({ recommendations: pkgs, recommendationSessionId: sessionId || null }),
      addToCart: (items) => {
        set((state) => {
          const newCart = [...state.cart];
          items.forEach(item => {
            const existing = newCart.find(i => i.product.id === item.product.id && i.frequency === item.frequency);
            if (existing) {
              existing.quantity += item.quantity;
              existing.subtotal = existing.quantity * existing.product.price;
            } else {
              newCart.push({ ...item, subtotal: item.quantity * item.product.price });
            }
          });
          return { cart: newCart };
        });
        debouncedSave();
      },
      removeFromCart: (productId) => {
        set((state) => ({
          cart: state.cart.filter(i => i.product.id !== productId)
        }));
        debouncedSave();
      },
      updateQuantity: (productId, quantity, frequency) => {
        set((state) => ({
          cart: state.cart.map(i =>
            i.product.id === productId
              ? { ...i, quantity, frequency: frequency || i.frequency, subtotal: quantity * i.product.price }
              : i
          )
        }));
        debouncedSave();
      },
      applyPromo: (code) => {
        const promo = CONSTANTS.PROMO_CODES.find(p => p.code === code.toUpperCase());
        if (promo) {
          set({ activePromo: promo as PromoCode });
          return true;
        }
        return false;
      },
      clearCart: () => {
        set({ cart: [], activePromo: null });
        debouncedSave();
      },
      resetWizard: () => set({ wizardStep: 0, recommendations: [], recommendationSessionId: null }),
      setCountry: (country) => set({ selectedCountry: country })
    }),
    {
      name: 'farmpack-app-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);